<template>
  <div>
    <SectionTitle>Additional Info</SectionTitle>

    <div class="flex items-center py-2" @click="photoClicked">
      <div class="w-20 h-16 flex-shrink-0 shadow-lg rounded-lg">
        <img v-if="photo" class="w-full h-full object-cover rounded-lg border-2 border-white" :src="photo.url" alt="" />
        <div v-else class="bg-gray-300 h-full w-full rounded-lg flex items-center justify-center">
          <i class="icon-image text-4xl text-gray-500"></i>
        </div>
      </div>

      <div v-if="photo" class="overflow-hidden px-3 text-xs text-gray-500 font-medium">
        <div class="font-semibold">PHOTO BY</div>
        <div class="truncate">{{ photo.createdBy }}</div>
        <div>{{ formatTimeAgo(photo.createdAt) }}</div>
      </div>
      <div v-else class="flex items-center text-gray-500 italic text-sm p-4">
        Tap <i class="icon-camera mx-2"></i> to take a photo
      </div>
    </div>

    <div class="my-4 rounded-md bg-gray-100 text-xs text-gray-500 flex justify-around items-center px-1">
      <BaseClearButton color="light" size="small" @click="takePhoto" :disabled="isUploadingPhoto">
        <span v-if="!isUploadingPhoto" class="flex items-center">
          <i class="icon-camera text-blue-500 mr-1"></i>
          <span>{{ photo ? 'Retake' : 'Take' }} Photo</span>
        </span>
        <span v-else class="flex items-center">
          <IonSpinner class="mr-1" name="dots" color="primary"></IonSpinner>
          <span>Uploading</span>
        </span>
      </BaseClearButton>
      <CommentsButton
        :title="commentsModalTitle"
        :comments="comments"
        :on-refresh="onRefresh"
        :on-add-comment="onAddComment"
        :on-remove-comment="onRemoveComment"
      ></CommentsButton>
    </div>

    <PhotoModal
      ref="photoModal"
      :photo="photo"
      :title="photoModalTitle"
      @take-photo="takePhoto"
      :is-uploading="isUploadingPhoto"
    />
  </div>
</template>

<script setup lang="ts">
import BaseClearButton from '@/components/forms/BaseClearButton.vue';
import SectionTitle from '@/components/forms/SectionTitle.vue';
import { useCamera } from '@/composables/use-camera';
import { useToast } from '@/composables/use-toast';
import PhotoModal from '@/components/photos/PhotoModal.vue';
import { IonSpinner } from '@ionic/vue';
import { IComment, IDocument } from '@ncss/models';
import { formatTimeAgo } from '@vueuse/core';
import { computed, ref } from 'vue';
import CommentsButton from '@/components/comments/CommentsButton.vue';

const { getPhoto } = useCamera();
const { errorToast } = useToast();

const props = defineProps<{
  comments?: IComment[];
  photo?: IDocument;
  context: 'GATEWAY' | 'REPEATER';
  onRefresh?: (e?: CustomEvent) => Promise<void>;
  onUploadPhoto?: (base64?: string) => Promise<void>;
  onAddComment?: (text: string) => Promise<void>;
  onRemoveComment?: (commentId: string) => Promise<void>;
}>();

const photoModalTitle = computed(() => (props.context === 'GATEWAY' ? 'Gateway Photo' : 'Repeater Photo'));
const commentsModalTitle = computed(() => (props.context === 'GATEWAY' ? 'Gateway Comments' : 'Repeater Comments'));
const photoModal = ref<InstanceType<typeof PhotoModal>>();
const isUploadingPhoto = ref(false);

function photoClicked() {
  if (props.photo) {
    photoModal.value?.present();
  } else {
    takePhoto();
  }
}

async function takePhoto() {
  if (!props.onUploadPhoto) {
    return;
  }
  const { error, base64, errorMsg } = await getPhoto();
  if (!error) {
    isUploadingPhoto.value = true;
    await props.onUploadPhoto(base64);
    isUploadingPhoto.value = false;
  } else if (errorMsg) {
    await errorToast(errorMsg);
  }
}
</script>
